import React, { useMemo } from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import Spinner from "../../../../components/shared/spinner/Spinner";
import { useSwingLibrarySettings } from "../../../swing_library/hooks/useSwingLibrarySettings";
import useColumnOrder from "../../../swing_library/components/Table/hooks/useColumnOrder";
import useHorizontalDragScroll from "../../../swing_library/components/Table/hooks/useHorizontalDragScroll";
import AvgRows from "../../../swing_library/components/Table/AvgRows/AvgRows";
import SortableHeader from "../../../swing_library/components/Table/SortableHeader";
import SummaryRow from "./SummaryRow";
import { LOCKED_COLUMN_COUNT } from "../../../swing_library/components/Table/constants/lockedColumnCount";
import { createDynamicIndicatorColumns } from "../../../swing_library/components/Table/utils/columnHelpers";

export default function IndicatorsTableView({
  collection1,
  collection2,
  isLoading,
  videosCollection1 = [],
  videosCollection2 = [],
}) {
  const dragScroll = useHorizontalDragScroll();
  const { data: swingLibrarySettings } = useSwingLibrarySettings();

  const combinedVideos = useMemo(() => {
    return [...(videosCollection1 ?? []), ...(videosCollection2 ?? [])];
  }, [videosCollection1, videosCollection2]);

  const collectionRows = useMemo(() => {
    const rows = [];

    if (collection1) {
      rows.push({
        backgroundClass: "bg-blue50",
        key: "collection1",
        title: collection1?.collectionName,
        videos: videosCollection1 ?? [],
      });
    }

    if (collection2) {
      rows.push({
        backgroundClass: "bg-pink50",
        key: "collection2",
        title: collection2?.collectionName,
        videos: videosCollection2 ?? [],
      });
    }

    return rows;
  }, [collection1, collection2, videosCollection1, videosCollection2]);

  const columns = useMemo(() => {
    const metaColumns = [
      {
        enableResizing: false,
        id: "meta-0",
        minSize: 100,
        size: 100,
        cell: () => null,
        header: () => null,
      },
      {
        enableResizing: false,
        id: "meta-1",
        minSize: 100,
        size: 100,
        cell: () => null,
        header: () => null,
      },
      {
        enableResizing: false,
        id: "meta-2",
        minSize: 100,
        size: 100,
        cell: () => null,
        header: () => null,
      },
      {
        enableResizing: false,
        id: "meta-3",
        minSize: 100,
        size: 100,
        cell: () => null,
        header: () => null,
      },
    ];

    const dynamicCols = createDynamicIndicatorColumns({
      savedIndicators: swingLibrarySettings?.savedIndicators ?? [],
      tourAveragesToggle: swingLibrarySettings?.tourAveragesToggle ?? false,
    });

    return [...metaColumns, ...dynamicCols];
  }, [
    swingLibrarySettings?.savedIndicators,
    swingLibrarySettings?.tourAveragesToggle,
  ]);

  const initialColumnOrder = useMemo(
    () => columns.map((column) => column.id),
    [columns]
  );

  const [columnOrder, handleDragEnd] = useColumnOrder(initialColumnOrder);

  const table = useReactTable({
    columnResizeDirection: "ltr",
    columnResizeMode: "onChange",
    columns,
    data: [],
    getCoreRowModel: getCoreRowModel(),
    state: { columnOrder },
  });

  const orderedMetricColumns = useMemo(() => {
    return columnOrder
      .map((id) => columns.find((column) => column.id === id))
      .filter(Boolean)
      .slice(LOCKED_COLUMN_COUNT);
  }, [columnOrder, columns]);

  if (isLoading) {
    return (
      <div className="border-gray200 flex items-center justify-center rounded-xl border bg-white py-6">
        <Spinner color="primary500" size="md" />
      </div>
    );
  }

  return (
    <div className="border-gray200 rounded-xl border bg-white">
      <div
        className="border-gray100 overflow-auto border-x"
        onMouseDown={dragScroll.onMouseDown}
        onMouseLeave={dragScroll.onMouseLeave}
        onMouseMove={dragScroll.onMouseMove}
        onMouseUp={dragScroll.onMouseUp}
        ref={dragScroll.ref}
      >
        <table className="w-full table-fixed border-collapse">
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <thead className="bg-white">
              <SortableContext
                items={
                  table.getHeaderGroups()[0]?.headers.map((h) => h.id) ?? []
                }
                strategy={verticalListSortingStrategy}
              >
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header, index) => (
                      <SortableHeader
                        columnIndex={index}
                        header={header}
                        key={header.id}
                      />
                    ))}
                  </tr>
                ))}
              </SortableContext>
            </thead>
          </DndContext>

          <tbody className="text-sm">
            <AvgRows
              allVideos={combinedVideos}
              columnOrder={columnOrder}
              columns={columns}
            />

            {collectionRows.map((row) => (
              <SummaryRow
                backgroundClass={row.backgroundClass}
                key={row.key}
                orderedMetricColumns={orderedMetricColumns}
                rowKey={row.key}
                title={row.title}
                videos={row.videos}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
